import React from "react";
import SectionWithButtonsLayout from "../../shared/layout/SectionWithButtonsLayout";
import GeneratedReportCard from "./GeneratedReportCard";
import DataTablesCard from "./DataTablesCard";
import DividerAccordionLayout from "../../shared/accordions/DividerAccordionLayout";
import CardSkeleton from "../../shared/loaders/CardSkeleton";
import { reportTypeOfTemplate } from "../../hooks/useReportCards";
import { useUserSettings } from "../../context/UserSettingsContext";
import { useProject } from "../../context/ProjectContext";
import { useDatasources } from "../../context/DatasourceContext";
import { useLoading } from "../../context/LoadingContext";
import { ReportTemplate } from "../../../excel/reports/api";
import { EdiphiReportTemplate } from "../../../excel/reports/templates/registry";
import { NO_PROJECT_EMPTY_STATE } from "../../constants";
import { REPORT_GROUP_META } from "./groupMeta";
import { ReportGroup, groupByReportGroup } from "../../../excel/reports/api/groups";
import { useCanAddDataTables } from "../../hooks/useCanAddDataTables";
import { UiStateCacheKey } from "../../../excel/workbook/cacheKeys";

interface ReportsSectionProps {
  handleAddClick: () => void;
  reports: ReportTemplate[];
  visibleTemplates: Record<string, EdiphiReportTemplate>;
  loadingReports: boolean;
  reloadReports: () => Promise<void>;
}

const SKELETON_COUNT = 3;

const groupCacheKey = (type: ReportGroup): UiStateCacheKey => `reportGroupExpanded_${type}`;

/**
 * The whole reports page as one section: a single Add, with type carried by dividers between groups
 * rather than by a header and button per type.
 */
const ReportsSection = ({
  handleAddClick,
  reports,
  visibleTemplates,
  loadingReports,
  reloadReports,
}: ReportsSectionProps) => {
  const { settings } = useUserSettings();
  const { selectedProject } = useProject();
  const { ediphiDatasources } = useDatasources();
  const { globalLoading } = useLoading();
  const canAddDataTables = useCanAddDataTables();

  const groups = groupByReportGroup(reports, reportTypeOfTemplate);

  // Reports unlock per datasource, so the gate is "is there anything to offer" rather than "is an
  // estimate loaded". getVisibleTemplates is that authority, and every template's renderIf keys off a
  // datasource's data — so GCGR reports become addable on their own once its selection is decoupled.
  // Or the Data Tables action, which the drawer offers on its own condition — the button has to
  // account for everything on offer there, not just templates.
  const canAdd = Object.keys(visibleTemplates).length > 0 || canAddDataTables;

  // Names what is missing rather than restating the state: no project points at the project selector,
  // nothing loaded points at the datasource cards. Deliberately not naming the estimate — any datasource
  // unlocks reports.
  const emptyStateText = !selectedProject
    ? NO_PROJECT_EMPTY_STATE
    : canAdd
      ? "Add a report to bring your Ediphi data into this workbook."
      : "Load data to get started.";

  const renderCards = () => {
    if (loadingReports) {
      return Array.from({ length: SKELETON_COUNT }, (_, index) => <CardSkeleton key={index} />);
    }

    const cards = groups.map(({ type, items }) => (
      <DividerAccordionLayout key={type} title={REPORT_GROUP_META[type].label} cacheKey={groupCacheKey(type)}>
        {items.map((report) => (
          <GeneratedReportCard key={report.worksheetName} template={report} onReload={reloadReports} />
        ))}
      </DividerAccordionLayout>
    ));

    // Data tables are a workbook item like the rest, so they sit in the list under Other rather than
    // in a section of their own. No report template maps to `other`, so this is the group's only member.
    // Gated on a datasource as well: the sheets outlive a cleared project, and a card for them would
    // otherwise be the only thing on a page that has nothing loaded.
    if (ediphiDatasources.length > 0 && settings.hasDataSheets) {
      cards.push(
        <DividerAccordionLayout key="other" title={REPORT_GROUP_META.other.label} cacheKey={groupCacheKey("other")}>
          <DataTablesCard />
        </DividerAccordionLayout>
      );
    }

    return cards.length > 0 ? cards : null;
  };

  return (
    <SectionWithButtonsLayout
      header={{ text: "Reports" }}
      primaryButton={{ onClick: handleAddClick, disabled: !canAdd || globalLoading }}
      emptyStateText={emptyStateText}
    >
      {renderCards()}
    </SectionWithButtonsLayout>
  );
};

export default ReportsSection;
